import {
  CheckboxField,
  Disclosure,
  Field,
  Input,
  Pill,
  Row,
  Rows,
  Section,
} from "@/components/kit";
import type { ConfigDef, FieldDef, FixtureTypeDef } from "@/lib/api/types";
import { toCode } from "@/lib/builder";

import { Fields, Names } from "./Fields";

/**
 * The same kind of fixture, played to different rules.
 *
 * Cricket's T20, ODI, Test and The Hundred. Rugby's fifteens and sevens. Chess's
 * classical and blitz. Kept apart from the kinds of fixture because the two
 * multiply — tennis is three kinds and two configs, not six kinds.
 *
 * The section is generated from the rules each kind declared, so an operator
 * never types a rule name twice and can never misspell one: the boxes are the
 * kind's own rules, and filling them in is the whole job.
 */

/** Which kinds a config covers. An empty list means all of them, and saying so
 * out loud matters — see the warning below. */
function covers(config: ConfigDef, code: string): boolean {
  return (config.appliesTo ?? []).length === 0 || (config.appliesTo ?? []).includes(code);
}

export function ConfigsSection({
  bare = false,
  configs,
  kinds,
  onChange,
}: {
  configs: ConfigDef[];
  kinds: FixtureTypeDef[];
  onChange: (configs: ConfigDef[]) => void;
  /** Render the contents without the section's own heading and rule.
   *  The wizard shows one section per step and supplies the heading itself, so
   *  the section's would be a second copy of it on the same screen. */
  bare?: boolean;
}) {
  const replace = (i: number, patch: Partial<ConfigDef>) =>
    onChange(configs.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  /** Kinds that have been given a name. A half-typed one has an empty code and
   *  belongs in no list an operator reads. */
  const named = kinds.filter((k) => k.code.trim() !== "");

  /** The rules a config can set: every rule declared by every kind it covers,
   * plus whatever it adds itself, minus whatever it drops. */
  const rulesFor = (config: ConfigDef): FieldDef[] => {
    const dropped = new Set(config.dropFields ?? []);
    const seen = new Map<string, FieldDef>();
    for (const kind of kinds) {
      if (!covers(config, kind.code)) continue;
      for (const rule of kind.format ?? []) {
        if (!dropped.has(rule.key)) seen.set(rule.key, rule);
      }
    }
    for (const added of config.addFields ?? []) seen.set(added.key, added);
    return [...seen.values()];
  };

  /** The trap found while testing the publish gate: a config with no
   * `appliesTo` covers every kind, so adding a kind quietly widens it onto
   * something that may not have those rules at all. Better said here, as it
   * happens, than as a validator message on a failed save. */
  const strandedBy = (config: ConfigDef): string[] => {
    const set = Object.keys(config.values ?? {});
    if (set.length === 0) return [];
    return kinds
      .filter((kind) => covers(config, kind.code))
      .filter((kind) => {
        const has = new Set((kind.format ?? []).map((f) => f.key));
        for (const added of config.addFields ?? []) has.add(added.key);
        return set.some((key) => !has.has(key));
      })
      .map((kind) => kind.label || kind.code);
  };

  const contents = (
    <>
      <Rows
        empty="None. A sport whose fixtures always follow the same rules needs none at all."
        count={configs.length}
        addLabel="Add a config"
        onAdd={() => onChange([...configs, { code: "", label: "", values: {} }])}
        onMove={(from, to) => {
          const next = [...configs];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          onChange(next);
        }}
        onRemove={(i) => onChange(configs.filter((_, j) => j !== i))}
      >
        {configs.map((config, i) => {
          const rules = rulesFor(config);
          const stranded = strandedBy(config);
          return (
            <Row key={i} index={i} badge={<Pill>{config.code || "no name yet"}</Pill>}>
              <Field label="Called" required htmlFor={`c-${i}-label`}>
                <Input
                  id={`c-${i}-label`}
                  value={config.label}
                  placeholder="T20"
                  onChange={(e) =>
                    replace(i, {
                      label: e.target.value,
                      code:
                        config.code === toCode(config.label) ? toCode(e.target.value) : config.code,
                    })
                  }
                />
              </Field>

              {/* Only worth asking with more than one kind. A sport with a single
                  kind of fixture has nothing to choose between, so the field was a
                  box whose only sensible answer was "leave it blank" — and its
                  placeholder was built by joining every kind's code, which on a
                  half-named kind produced a bare comma. */}
              {named.length > 1 && (
                <Names
                  id={`c-${i}-applies`}
                  label="For which kinds"
                  hint={
                    (config.appliesTo ?? []).length === 0
                      ? `Blank covers all ${named.length}`
                      : "By name, comma separated"
                  }
                  placeholder={named.map((k) => k.code).join(", ")}
                  values={config.appliesTo ?? []}
                  onChange={(appliesTo) => replace(i, { appliesTo })}
                />
              )}

              {/* On the control line, not the label line — see `CheckboxField`. */}
              <CheckboxField
                label="Retired"
                checked={config.hidden ?? false}
                onChange={(e) => replace(i, { hidden: e.target.checked })}
              />

              {stranded.length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Pill tone="warn" dot>
                    {stranded.join(", ")} {stranded.length === 1 ? "has" : "have"} no such rule
                  </Pill>
                  <p style={{ margin: "var(--s1) 0 0", fontSize: "var(--t-xs)", color: "var(--ink-3)" }}>
                    This config sets a rule those kinds do not declare, so publishing will be
                    refused. Either name the kinds it is for, or add the rule to them.
                  </p>
                </div>
              )}

              {rules.length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Disclosure
                    title="The rules, as this config plays them"
                    note="Every rule declared by the kinds this covers. Filling them in is the whole job of a config."
                    count={Object.keys(config.values ?? {}).length}
                  >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: "var(--s3) var(--s4)",
                    }}
                  >
                    {rules.map((rule) => (
                      <Field key={rule.key} label={rule.label ?? rule.key} htmlFor={`c-${i}-${rule.key}`}>
                        <Input
                          id={`c-${i}-${rule.key}`}
                          type={
                            rule.type === "integer" || rule.type === "number" ? "number" : "text"
                          }
                          value={String((config.values ?? {})[rule.key] ?? "")}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const values = { ...(config.values ?? {}) };
                            if (raw === "") delete values[rule.key];
                            else
                              values[rule.key] =
                                rule.type === "integer" || rule.type === "number"
                                  ? Number(raw)
                                  : raw;
                            replace(i, { values });
                          }}
                        />
                      </Field>
                    ))}
                  </div>
                  </Disclosure>
                </div>
              )}

              {/* The two that change the *shape* of the rules rather than their
                  answers. Rare, and behind one heading rather than loose in the
                  card: most configs only fill the boxes above in. */}
              <div style={{ gridColumn: "1 / -1" }}>
                <Disclosure
                  title="Change which rules exist"
                  note="The Hundred has no overs at all, and counts a hundred balls in sets of five instead. Dropping a rule and adding one are how a config says that, rather than a second kind of fixture that is really the same contest."
                  count={(config.dropFields ?? []).length + (config.addFields ?? []).length}
                >
                  <Names
                    id={`c-${i}-drop`}
                    label="Rules that do not apply here"
                    hint="By name — overs"
                    placeholder="overs"
                    values={config.dropFields ?? []}
                    onChange={(dropFields) => replace(i, { dropFields })}
                  />
                  <Fields
                    what="extra rule"
                    fields={config.addFields ?? []}
                    masters={[]}
                    onChange={(addFields) => replace(i, { addFields })}
                  />
                </Disclosure>
              </div>
            </Row>
          );
        })}
      </Rows>
    </>
  );

  if (bare) return contents;

  return (
    <Section
      title="Configs"
      note="Same shape, different rules — T20 and Test, sevens and fifteens. Picked in one step when a fixture is added, and its answers are copied onto that fixture there and then."
    >
      {contents}
    </Section>
  );
}
